import type {
  TransactionOrder,
  TransactionStatus,
} from '../components/CardProductTransaction/CardProductTransaction';
import type { OrderDisplayStatus, OrderHistoryRow } from '../types/order.types';
import { authorLabel } from './bundle';

/** Card status (badge colours and buttons) per order display status. */
export const TRANSACTION_STATUS: Record<OrderDisplayStatus, TransactionStatus> = {
  pending: 'waiting',
  processing: 'process',
  shipped: 'delivery',
  arrived: 'come',
  completed: 'finish',
  cancelled: 'cancel',
  // No tab of its own: shown under Semua like a cancelled order, with its own label
  refunded: 'cancel',
};

const TIME_ZONE = 'Asia/Jakarta';

const dateFormat = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: TIME_ZONE,
});

const timeFormat = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: TIME_ZONE,
});

/** e.g. 9 Sep 2026 (Jakarta time) */
export const formatOrderDate = (iso: string) => dateFormat.format(new Date(iso));

/** e.g. 9 Sep 2026 23:59 WIB */
export const formatPaymentDeadline = (iso: string) =>
  `${formatOrderDate(iso)} ${timeFormat.format(new Date(iso))} WIB`;

/** e.g. 25 Sep 2026 - 15:51 WIB */
export const formatTrackingTime = (iso: string) =>
  `${formatOrderDate(iso)} - ${timeFormat.format(new Date(iso))} WIB`;

export const toTransactionOrder = (row: OrderHistoryRow): TransactionOrder => {
  const status = TRANSACTION_STATUS[row.display_status];
  const shipment = row.shipment;
  const event = shipment?.latest_event;

  return {
    id: row.order_id,
    invoice: row.order_number,
    date: formatOrderDate(row.placed_at),
    status,
    statusLabel:
      row.display_status === 'refunded'
        ? (row.display_status_label ?? 'Dikembalikan')
        : row.transfer_status === 'reported'
          ? 'Menunggu Konfirmasi'
          : undefined,
    awaitingConfirmation: row.transfer_status === 'reported',
    paymentDeadline: row.payment_expires_at
      ? formatPaymentDeadline(row.payment_expires_at)
      : undefined,
    items: row.items.map((item) => ({
      quantity: item.quantity,
      product: {
        id: item.order_item_id,
        title: item.title,
        author: authorLabel(item),
        frontCover: item.cover_url,
        // Charged unit price: line_total is the final price x quantity
        price: Number(item.line_total) / item.quantity,
      },
    })),
    totalPayment: Number(row.grand_total),
    shipping: shipment?.tracking_number
      ? {
          courier: shipment.courier_name ?? shipment.courier_code.toUpperCase(),
          trackingNumber: shipment.tracking_number,
          note: event
            ? [event.description, event.location].filter(Boolean).join(' • ') || undefined
            : undefined,
        }
      : undefined,
  };
};
